import { CalLinkType } from "src/shared/constants/calLinks";
import { VocationId } from "src/types/Vocation";

/**
 * Date-range presets for the admin "Training Health" dashboard (PR 5b).
 * Used by every per-cohort hook to bound Firestore reads and namespace the
 * react-query cache key per range.
 */
export type TrainingHealthRange = "7d" | "14d" | "30d" | "90d" | "all";

/**
 * Shared react-query options for admin analytics hooks (Training Health
 * cards + sibling dashboards). Disables window-focus refetch so the
 * dashboard's 6+ parallel queries don't refire as a burst on tab focus —
 * that burst, racing with the userSummaries scan, was the trigger for the
 * Firestore "too many outstanding requests" error.
 *
 * Spread into both `useAppQuery` and `useInfiniteQuery` call sites.
 * Scoped to admin analytics intentionally — app-wide focus-refetch is
 * still on for student-facing queries where data freshness matters.
 */
export const ADMIN_ANALYTICS_QUERY_OPTIONS = {
  refetchOnWindowFocus: false,
} as const;

const queryKeys = {
  aiConfigChanges: (system: "autodraft" | "reimbursements" | "search") =>
    ["aiConfigChanges", system] as const,
  aiKnowledgeBase: () => ["aiKnowledgeBase"] as const,
  aiMissedLookups: () => ["aiMissedLookups"] as const,
  agentRolloutConfig: () => ["agentRolloutConfig"] as const,
  autodraftConfig: () => ["autodraftConfig"] as const,
  // AI Console token pricing map (appConfig/aiPricing). One org-wide key; the
  // rates change only when a model is repriced, so the hook caches for an hour.
  aiPricing: () => ["aiPricing"] as const,
  autodraftOverview: () => ["autodraftOverview"] as const,
  searchTuningOverview: () => ["searchTuningOverview"] as const,
  // Impact report for an organization externalIdentifier (e.g. "mocj"). Keyed by
  // identifier so each dashboard caches separately. The callable reads nightly
  // snapshots, so the hook caches its result client-side for 24h (see
  // useOrganizationImpactReport) — a report is fetched at most once per day.
  organizationImpactReport: (externalIdentifier: string) =>
    ["organizationImpactReport", externalIdentifier] as const,
  // Sales Enablement → District/Zipcode Constituents. One org-wide aggregation
  // over the hourly `userSummaries` projection, so a single key with no
  // parameters; the screen filters and drills down client-side.
  districtConstituentAggregation: () =>
    ["districtConstituentAggregation"] as const,
  autodraftThroughput: (windowDays: number) =>
    ["autodraftThroughput", windowDays] as const,
  // Every reimbursement request, org-wide (AI Console Throughput). One key; the
  // tab windows and partitions the rows client-side.
  reimbursementRows: () => ["reimbursementRows"] as const,
  resumeWorkExperience: (userId: string) =>
    ["resumeWorkExperience", userId] as const,
  // One student's latest coach note (the Kanban card / Job placement tab /
  // Dial rail note). Patched in place after a save.
  latestCoachNote: (userId: string) => ["latestCoachNote", userId] as const,
  reimbursementTuning: () => ["reimbursementTuning"] as const,
  themeWorkspace: () => ["themeWorkspace"] as const,
  // Operator-edited Dial slash snippets (appConfig/dialSnippets).
  dialSnippets: () => ["dialSnippets"] as const,
  dialSnippetConfig: () => ["dialSnippetConfig"] as const,
  partnerContacts: () => ["partnerContacts"] as const,
  // The Outreach Dial rail's open cases. `sort` distinguishes the triage
  // ranking from the recency (iMessage) view so switching refetches; omit it
  // for a prefix that matches every sort variant (cache patches/invalidation).
  outreachCaseQueue: (sort?: string) =>
    sort
      ? (["outreachCaseQueue", sort] as const)
      : (["outreachCaseQueue"] as const),
  // useAtoms loads every atom (active + archived) under one key and
  // partitions client-side, so the key is just the vocation.
  atoms: (vocationId: VocationId) => ["atoms", vocationId] as const,
  vocation: (vocationId?: VocationId) => ["vocation", vocationId] as const,
  vocations: () => ["vocations"] as const,
  publicVocation: (vocationId?: VocationId) =>
    ["publicVocation", vocationId] as const,
  calcomBooking: (userId: string, linkId: string) =>
    ["calcomBooking", userId, linkId] as const,
  employers: () => ["employers"] as const,
  jobPositions: (employerId: string) => ["jobPositions", employerId] as const,
  // Prefix key: invalidating this hits every jobPositions query, including
  // the board's all-employers fetch below.
  jobPositionsRoot: () => ["jobPositions"] as const,
  // Every employer's positions, flattened, for the Jobs & employers board.
  // Keyed under the jobPositions root so position writes invalidate it too.
  allJobPositions: (employerIds: readonly string[]) =>
    ["jobPositions", "all", employerIds] as const,
  // All active job positions under active partner employers, flattened. Feeds
  // the student "Jobs for you" matching hook; student-side matching happens in
  // memory off this org-wide list.
  activePartnerJobPositions: () => ["activePartnerJobPositions"] as const,
  // All active job positions under ALL active saved employers (any
  // partnerStatus) — the Jobs-board universe, for the Job placement tab and
  // the Referral dashboard. Keyed under the jobPositions root so position
  // writes invalidate it too.
  activeEmployerJobPositions: () => ["jobPositions", "activeAll"] as const,
  // Each active position's policy text hash and Journey allowlist flag, for
  // the eligibility review queue. Under the jobPositions root so a policy or
  // allowlist edit invalidates it.
  reviewQueuePositions: () =>
    ["jobPositions", "eligibilityReviewQueue"] as const,
  // Every cache of eligibility verdicts lives under this root, so one
  // patch or invalidate reaches them all (see patchCachedEligibilityVerdict).
  allEligibilityVerdicts: () => ["eligibilityVerdicts"] as const,
  // Per-student background-check-vs-employer-policy verdicts (admin-only
  // subcollection read); invalidated after an evaluation run.
  eligibilityVerdicts: (userId: string) =>
    ["eligibilityVerdicts", "student", userId] as const,
  // The same student's verdicts narrowed to a set of job positions (the Dial
  // rail reads only their Journey pool). Extends the per-student key so an
  // evaluation run's invalidation reaches it. Ids sorted so order doesn't
  // refetch.
  eligibilityVerdictsForStudentJobs: (
    userId: string,
    jobPositionIds: readonly string[]
  ) =>
    [
      "eligibilityVerdicts",
      "student",
      userId,
      "jobs",
      [...jobPositionIds].sort(),
    ] as const,
  // What a student told Journey about felony convictions (admin-only field on
  // `confidentialUserInformation/{userId}`). Its own root on purpose: the
  // eligibilityVerdicts root is patched as verdict lists.
  journeyFelonyDisclosure: (userId: string) =>
    ["journeyFelonyDisclosure", userId] as const,
  // Post-program verification (FCRA) authorizations for one student. Server
  // writes only; invalidated after signing or an admin revocation. Never holds
  // the SSN (the doc stores only the last 4).
  fcraAuthorizations: (userId: string) =>
    ["fcraAuthorizations", userId] as const,
  // Journey call records for one student (server-written; coach reads).
  jobMatchCalls: (userId: string) => ["jobMatch", "calls", userId] as const,
  // Journey job matches for one student (server- or admin-written; the
  // coach tab and the student's plan read them).
  jobMatches: (userId: string) => ["jobMatch", "matches", userId] as const,
  // One student's verified employment outcomes (coach tab: link prompt).
  studentEmploymentOutcomes: (userId: string) =>
    ["employmentOutcomes", "student", userId] as const,
  // The affidavit behind a signing link (no login; callable read).
  jobOfferIncomeAffidavitByLink: (outcomeId: string) =>
    ["employmentOutcomes", "affidavitLink", outcomeId] as const,
  // Every student's active job matches (admin-only collection-group read),
  // for the Referral dashboard's Pipeline tab. Invalidated by the coach
  // add / remove match actions alongside the per-student key.
  jobMatchBoard: () => ["jobMatch", "board"] as const,
  // Every student's turned-down matches, for the By student board's "turned
  // down" chips. Its own root, so only a match removal re-reads it.
  turnedDownJobMatchBoard: () => ["jobMatch", "turnedDown"] as const,
  // One admin doc (a student's coach, for the coach booking link).
  coachAdmin: (adminId: string) => ["admins", "coach", adminId] as const,
  // One student's full jobApplications list (direct read, not the summary
  // denorm); invalidated by the coach "+ Applied" / "×" actions.
  studentJobApplications: (userId: string) =>
    ["jobApplications", "student", userId] as const,
  // The same student's applications narrowed to a set of employers (the Job
  // matches board reads one of these per matched student). Extends the
  // per-student key so the coach actions' prefix invalidation reaches it.
  // Ids are sorted so set order doesn't refetch.
  studentJobApplicationsForEmployers: (
    userId: string,
    employerIds: readonly string[]
  ) =>
    [
      "jobApplications",
      "student",
      userId,
      "employers",
      [...employerIds].sort(),
    ] as const,
  // Employer-scoped inverse: all students' verdicts for a set of job
  // positions (admin-only collection-group read), for the Background
  // eligibility module. Ids are sorted so scope order doesn't refetch.
  eligibilityVerdictsForJobPositions: (jobPositionIds: readonly string[]) =>
    [
      "eligibilityVerdicts",
      "jobPositions",
      [...jobPositionIds].sort(),
    ] as const,
  // Prefix matching every scope above, for invalidating or patching them all.
  allEligibilityVerdictsForJobPositions: () =>
    ["eligibilityVerdicts", "jobPositions"] as const,
  // Every student's model-produced verdict, behind the Eligibility Review queue.
  modelEligibilityVerdicts: () => ["eligibilityVerdicts", "model"] as const,
  // Ids of students set to job searching, for the review queue's ordering.
  jobSearchingUserIds: () => ["users", "jobSearchingIds"] as const,
  // Coach → employer referrals for one student (all statuses), for the
  // profile "Referred to" picker. Invalidated after refer / close writes.
  employerReferrals: (userId: string) => ["employerReferrals", userId] as const,
  // Open (`referred`) referrals across students, for the Referral dashboard.
  openEmployerReferrals: () => ["openEmployerReferrals"] as const,
  // Closed (`not_hired`) referrals across students, so the dashboard boards can
  // show a struck-through pill instead of the referral vanishing on close.
  closedEmployerReferrals: () => ["closedEmployerReferrals"] as const,
  // PR 4.5b — verified employment outcomes that carry a free-text company but
  // no employerId, grouped by company string for the reconciliation module.
  // Single org-wide key (admin-only collection-group read); invalidated after
  // a link / create-from-outcome write so reconciled groups drop out.
  unlinkedOutcomeGroups: () => ["unlinkedOutcomeGroups"] as const,
  // Employment outcomes linked to each employer (grouped, not aggregated), for
  // the saved-employers "Outcomes" column. Filter-independent: the page's
  // state/cohort/coach filter re-tallies these rows client-side.
  outcomeRowsByEmployer: (employerIds: readonly string[]) =>
    ["outcomeRowsByEmployer", employerIds] as const,
  // Prefix key: invalidates every per-employer-set fetch after a link write.
  allOutcomeRows: () => ["outcomeRowsByEmployer"] as const,
  // (student, record type) pairs linked to a single employer, for the
  // saved-employers "Outcomes" drilldown (grouped by record type). On-demand:
  // enabled only while the drilldown is open.
  outcomeUserLinksByEmployer: (employerId: string) =>
    ["outcomeUserLinksByEmployer", employerId] as const,
  // Prefix key: invalidates every per-employer drilldown after a link write
  // (mirrors allOutcomeRows — the exact employerId isn't always known there).
  allOutcomeUserLinks: () => ["outcomeUserLinksByEmployer"] as const,
  // NOTE: the Employer Network dashboard's per-employer placement stats and
  // partner-share count are no longer Firestore queries — they're derived
  // client-side from the loaded `UserSummary[]`
  // (see deriveEmployerPlacementStatsFromSummaries), so they have no query keys.
  calcomSlots: (
    linkId: CalLinkType,
    rangeStart: string | undefined,
    rangeEnd: string | undefined,
    timeZone: string
  ) => ["calcomSlots", linkId, rangeStart, rangeEnd, timeZone] as const,
  grades: (uid: string) => ["grades", uid] as const,
  referralCode: (userId: string) => ["referralCode", userId] as const,
  referralCounts: (userId: string) => ["referralCounts", userId] as const,
  studentReferrers: () => ["studentReferrers"] as const,
  partnerStudentStories: (partnerId: string) =>
    ["partnerStudentStories", partnerId] as const,
  partnerMediaAssets: (partnerId: string) =>
    ["partnerMediaAssets", partnerId] as const,
  partnerLinkStats: (partnerId: string) =>
    ["partnerLinkStats", partnerId] as const,
  quizQuestions: (filter: {
    vocationId?: VocationId;
    atomId?: string;
    includeArchived?: boolean;
    /** Exam mode narrows the fetch to one category's atoms — see useQuizQuestions. */
    categoryId?: string;
  }) => ["quizQuestions", filter] as const,
  // `sort` distinguishes the priority-ranked views from the recency (iMessage)
  // view so switching refetches. Omit it for a stage-wide prefix — invalidation
  // and the close-prune match every variant under the stage. The trailing scope
  // object carries the "My caseload" coach and whether add-on (call-triage-only)
  // cases are included, so those variants cache separately: Triage and Call
  // Triage share the priority sort and differ ONLY by that flag, so without it
  // they would collide on one cache entry.
  triageQueue: (
    stage: string,
    sort?: string,
    assignedCoachUid?: string,
    includeAddOns?: boolean
  ) =>
    sort
      ? ([
          "triageQueue",
          stage,
          sort,
          {
            assignedCoachUid: assignedCoachUid ?? null,
            includeAddOns: Boolean(includeAddOns),
          },
        ] as const)
      : (["triageQueue", stage] as const),
  queueCounts: () => ["queueCounts"] as const,
  // Open-case count for one stage assigned to a coach — the "My caseload" badge.
  // Omit the coach for a stage-wide prefix (invalidation matches every coach).
  myCaseloadCount: (stage: string, assignedCoachUid?: string) =>
    assignedCoachUid
      ? (["myCaseloadCount", stage, assignedCoachUid] as const)
      : (["myCaseloadCount", stage] as const),
  // Success Queue PR4b — the pending AI suggestion behind the composer, keyed
  // by the AgentDecision id so approve/suppress can invalidate just this case.
  agentDecision: (decisionId: string) => ["agentDecision", decisionId] as const,
  // The immutable rationale snapshot behind a suggestion, keyed by (caseId,
  // version) — lazily read by the "Why this message?" expander, so it's cached
  // independently of the queue/decision.
  signalArray: (caseId: string, version: number) =>
    ["signalArray", caseId, version] as const,
  // Right-rail case-history tab — recent closed cases for a student, gated by
  // the active tab.
  closedCaseHistory: (userId: string) => ["closedCaseHistory", userId] as const,
  // Success Queue name search — keyed by the (debounced) term so each query is
  // cached independently; enabled only past the minimum length.
  studentSearch: (term: string) => ["studentSearch", term] as const,
  // The full UserAccount behind a case, for the right-rail profile checklist —
  // loaded only when the Profile tab is active.
  caseStudentProfile: (userId: string) =>
    ["caseStudentProfile", userId] as const,
  // Right-rail Notes tab — the student's profile notes (same source as the
  // ClientScreen Notes tab), loaded only when the Notes tab is active.
  caseStudentNotes: (userId: string) => ["caseStudentNotes", userId] as const,
  // "Send financial aid" modal — the student's financial-aid + reimbursement
  // history, loaded only while that modal is open (never on the queue path).
  caseStudentFinancialAid: (userId: string) =>
    ["caseStudentFinancialAid", userId] as const,
  caseStudentReimbursements: (userId: string) =>
    ["caseStudentReimbursements", userId] as const,
  // Tremendous campaigns for an org, for the "Send financial aid" campaign
  // picker. Keyed by orgId so it's shared across that org's students.
  orgReimbursementCampaigns: (organizationId: string) =>
    ["orgReimbursementCampaigns", organizationId] as const,
  // The student's assigned training school, for the header placement pill.
  // Keyed by schoolId so it's shared across cases at the same school.
  caseStudentSchool: (schoolId: string) =>
    ["caseStudentSchool", schoolId] as const,
  // Right-rail Training tab — the student's userSummary, the single doc the
  // theory-training checklist derives from. Loaded only when that tab is active
  // (never on the queue-list path), cached by userId.
  caseStudentSummary: (userId: string) =>
    ["caseStudentSummary", userId] as const,
  // Right-rail Profile tab (yard_training) — the student's recent attendance
  // check-ins reduced to latest skills + latest concern. Loaded only when that
  // tab is active for a yard-training case (never on the queue-list path),
  // cached by userId.
  caseStudentAttendance: (userId: string) =>
    ["caseStudentAttendance", userId] as const,
  // Right-rail Profile tab (yard_training) — lifetime count of attended
  // sessions. Absence check-ins are excluded by the service query.
  caseStudentCompletedSessions: (userId: string) =>
    ["caseStudentCompletedSessions", userId] as const,
  // Success Queue SMS composer — server-previewed sender number, scoped by
  // admin because MOCJ human routing can depend on the authenticated coach.
  smsSendRoutePreview: (userId: string, line: string, adminId: string) =>
    ["smsSendRoutePreview", userId, line, adminId] as const,
  // Resolved download URL for a private storage path (thread MMS images).
  // Keyed by path so every bubble showing the same image shares one resolve.
  storageDownloadUrl: (path: string) => ["storageDownloadUrl", path] as const,
  // Right-rail Profile tab (yard_training) — recent (7-day) attendance rate,
  // computed from the check-in subcollection rather than the appointment-derived
  // summary %, which reads null for yard students. Cached by userId.
  caseStudentAttendanceRate: (userId: string) =>
    ["caseStudentAttendanceRate", userId] as const,
  // Auto-captured operator time on a case, split by surface — the active-time
  // breakdown shown in the case bar + close modal.
  caseTimeEntries: (caseId: string) => ["caseTimeEntries", caseId] as const,
  // Active draft-theme classifications offered by the close modal. Themes are
  // stage-scoped and change infrequently, so every case in a stage shares one
  // cache entry.
  caseCloseThemes: (stage: string) => ["caseCloseThemes", stage] as const,
  // Answered-call talk time on a case (the "call" slice), summed from the
  // student's call notes since the active-time window start. Keyed by window so
  // a reopen (new sinceMillis) busts the cache.
  caseCallSeconds: (userId: string, sinceMillis: number) =>
    ["caseCallSeconds", userId, sinceMillis] as const,
  // PR 5b — admin "Training Health" dashboard hooks. One key per card, scoped
  // to (cohortId, range) so changing either filter blows the relevant cache
  // entry without thrashing unrelated keys. No cursor in the key for
  // concerns/selfie infinite-queries — react-query's useInfiniteQuery
  // manages page accumulation internally.
  trainingHealthCheckInRate: (cohortId: string, range: TrainingHealthRange) =>
    ["trainingHealthCheckInRate", cohortId, range] as const,
  // Skills Training Analytics userSummaries scan. One org-wide key so the
  // screen and the Training Health tab share a single fetch.
  skillsTrainingUserSummaries: () => ["skillsTrainingUserSummaries"] as const,
  // Enrollment Compliance tab — every enrollment-interview appointment for
  // the selected cohorts (one collectionGroup read per 30-cohort chunk ×
  // interview slug), joined client-side to the userSummaries by userId. Keyed
  // by the sorted cohort-id set and the sorted interview-slug set so [A,B] and
  // [B,A] share a cache entry and a new vocation's slug busts the cache.
  enrollmentInterviews: (
    cohortIds: readonly string[],
    interviewSlugs: readonly string[]
  ) => ["enrollmentInterviews", cohortIds, interviewSlugs] as const,
  // Union of enrollment-interview Cal.com slugs across all vocations. Content
  // is CMS-like and vocation-agnostic, so a single global cache entry.
  enrollmentInterviewSlugs: () => ["enrollmentInterviewSlugs"] as const,
  permitTimeseries: (cohortIds: readonly string[], sinceDate: string) =>
    ["permitTimeseries", cohortIds, sinceDate] as const,
  trainingHealthHoursPerWeek: (cohortId: string, range: TrainingHealthRange) =>
    ["trainingHealthHoursPerWeek", cohortId, range] as const,
  trainingHealthSkillCoverage: (cohortId: string, range: TrainingHealthRange) =>
    ["trainingHealthSkillCoverage", cohortId, range] as const,
  trainingHealthConcernsFeed: (
    cohortId: string,
    category: "school" | "personal" | "absence" | "all"
  ) => ["trainingHealthConcernsFeed", cohortId, category] as const,
  trainingHealthTestDateRollup: (cohortId: string) =>
    ["trainingHealthTestDateRollup", cohortId] as const,
  trainingHealthSelfieGallery: (cohortId: string, range: TrainingHealthRange) =>
    ["trainingHealthSelfieGallery", cohortId, range] as const,
  vendorForms: (uid: string) => ["vendorForms", uid] as const,
  vendorLocations: (vendorIds: readonly string[]) =>
    ["vendorLocations", vendorIds] as const,
  // PR-2 — post-course permit-attainment wizard. Two derived KPI keys for the
  // dashboard. `weeklyExamCount` and `streak` are keyed on the weekStartISO +
  // timeZone so a TZ change or week rollover busts the cache without
  // retaining stale prior-week numbers. The plan itself is read inline from
  // `useCurrentUser().theoryTrainingPlan?.practicePlan` — no separate query.
  weeklyExamCount: (userId: string, weekStartISO: string, timeZone: string) =>
    ["weeklyExamCount", userId, weekStartISO, timeZone] as const,
  streak: (userId: string, weekStartISO: string, timeZone: string) =>
    ["streak", userId, weekStartISO, timeZone] as const,
  // Admin New Vocations dashboard — every applicant to a trades program
  // (Diesel Tech, HVAC/R). One org-wide key; the screen splits applicants vs
  // accepted roster client-side.
  newVocationUserSummaries: () => ["newVocationUserSummaries"] as const,
};

export default queryKeys;
